import NavigationItem from '@/classes/navigationItem';
import { canAccess } from '@/utils/acl';
import { getAccess } from '@/app-instance';

const defaultState = () => {
  return {
    defaultNavigationItemId: 0,
    navigationItems: {},
  };
};

const getters = {
  dashboardItems: (state, getters) => getters.navigationItemsList.filter(ni => ni.showOnDashboard).sort((a, b) => a.dashboardOrder - b.dashboardOrder),
  defaultNavigationItem: (state, getters) => getters.menuItems.find(ni => ni.id === state.defaultNavigationItemId),
  menuItems: (state, getters, rootState, rootGetters) => {
    const menuGroup = rootGetters['navigationGroups/menuNavigationGroup'];
    if (menuGroup) {
      const navItems = menuGroup.navigationItems({ getters: rootGetters });
      const user = rootGetters['user/get'];
      const a = navItems.filter(ni => {
        // Dashboards are purchasable and we need to determine the access to a given
        // dashboard in the menu to filter out any dashboards that a user does not
        // have access to. This is the same for the dadashboard groups but the user
        // shouldn't see the nav item if none of the dashboards in the group are
        // accessible.
        if (ni.destination === 'dashboard') {
          // First we need to grab the dashboard specified in the nav item
          const dashboard = rootGetters['dashboards/get'][ni.resourceId];
          if (!dashboard) return false;
          // Once we have the dashboard, we can check if the user has access to it
          // so long as the hiddenShowOnPurchase flag is set for the given dashboard
          if (dashboard?.hiddenShowOnPurchase) {
            return canAccess(user, dashboard);
          } else {
            return true;
          }
        } else if (ni.destination === 'dashboard_group') {
          // We need to grab the nav group that the dashboard_group nav item points to
          const navGroup = rootGetters['navigationGroups/navigationGroups'][ni.resourceId];
          // Once we get the nav group, we can grab the n nav items in that nav group and
          // map over them to grab the dashboard that each nav item points to.
          const dashboards = navGroup.navigationItems({ getters: rootGetters })
            .map(navItem => rootGetters['dashboards/get'][navItem.resourceId]);
          // Now that we have the dashboards, we can do through each dashboard and determine
          // if a user can access any of dashboards in the dashboard group. If the dashboard
          // has the hiddenShowOnPurchase flag set, we check access otherwise we can just
          // say that the user does have access ot it
          return dashboards.some(dash => {
            if (dash?.hiddenShowOnPurchase) {
              return canAccess(user, dash);
            } else {
              return true;
            }
          });
        } else {
          return true;
        }
      });
      return a;
    } else {
      return [];
    }
  },
  navigationItems: state => state.navigationItems,
  navigationItemsList: (state, getters, rootState, rootGetters) => {
    let items = Object.keys(state.navigationItems)
      .map(id => state.navigationItems[id])
      .sort((a, b) => a.order - b.order);

    // if user has purchased tiered but not subscribed, hide nav items specified in product var if it exists
    // eventually we need to move this into something like an access control filter
    if (rootGetters['session/hasPurchasedTiered'] && !rootGetters['session/isSubscribed']) {
      const pv = rootGetters['productVars/get']['hiddenNavItemIdsTieredUsers'];
      if (pv) {
        let ids = JSON.parse(pv.value);
        items = items.filter(ni => !ids.includes(ni.id));
      }
    }

    return items;
  },
};

const mutations = {
  setDefaultNavigationItem: function(state, id) {
    state.defaultNavigationItemId = id;
  },
  setNavigationItems: function(state, items) {
    items.forEach(item => {
      const navigationItem = new NavigationItem(item);
      // if nav item isn't supported, we shouldn't add it to the store
      if (navigationItem.route) {
        state.navigationItems[navigationItem.id] = navigationItem;
      }
    });
  },
  setDynamicNavigationItems(state, rootGetters) {
    if (rootGetters['navigationItems/defaultNavigationItem'] && rootGetters['navigationItems/defaultNavigationItem'].destination === 'user_certifications') {
      let lastId = (rootGetters['navigationItems/navigationItemsList'].slice(-1).pop() || {}).id;
      let lastOrder = (rootGetters['navigationItems/navigationItemsList'].slice(-1).pop() || {}).order;
      let menuGroupId = (rootGetters['navigationGroups/menuNavigationGroup'] || {}).id;
      let certifications = rootGetters['certifications/certifications'];
      let userCertifications = rootGetters['userCertifications/userCertificationsList'];
      let navItems = [];
      // Create a store-like object for $access function (it expects store.getters)
      const storeProxy = { getters: rootGetters };
      const $access = getAccess();

      for (let uc in userCertifications) {
        let userCertification = userCertifications[uc];
        let name = `${certifications[userCertification.certificationId].certificateNumber} ${certifications[userCertification.certificationId].name}`;
        // we need to get acl stuff out of a mixin to be used here
        let hasAccess = $access && $access(storeProxy, 'category', certifications[userCertification.certificationId].categoryId);
        let icon = hasAccess ? 'questions' : 'lock';
        const item = new NavigationItem({
          destination: 'user_certification',
          dynamic: true,
          id: ++lastId,
          icon: icon,
          name: name,
          navigationGroupId: menuGroupId,
          resourceId: userCertification.id,
        });
        navItems.push(item);
      }
      navItems = navItems.sort( (a, b) => a.name.localeCompare(b.name) );
      navItems.forEach( item => item.order = ++lastOrder );
      navItems.forEach( item => { state.navigationItems[item.id] = item; });
    }
  },
  resetDynamicNavigationItems(state) {
    Object.keys(state.navigationItems).map(id => {
      if (state.navigationItems[id].dynamic) {
        delete state.navigationItems[id];
      }
    });
  },
  reset(state) {
    Object.assign(state, defaultState());
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
};
